import { useParams, Link } from 'react-router-dom';
import SEOHelmet from '@/components/feature/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(category?.id || '') : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类不存在</h2>
            <p className="text-sm text-foreground-500 mb-6">这个分类可能已经被移除或从未存在过</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHelmet
        title={`${category.name} - 黑料不打烊`}
        description={category.description}
        keywords={`${category.name},黑料不打烊,明星黑料爆料,娱乐圈内幕`}
        canonicalUrl={`https://www.heiliaobudayang.com/category/${category.slug}`}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">{category.name}</span>
          </nav>

          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className={`${category.icon} text-lg text-primary-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-400">
              <i className="ri-article-line"></i>
              <span>共 <strong className="text-primary-600">{articles.length}</strong> 篇爆料</span>
            </div>
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无爆料，敬请期待</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 bg-accent-500 text-white text-xs font-bold rounded-md">
                        {article.categoryName}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 mt-2 line-clamp-2">{article.excerpt}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                      <span className="whitespace-nowrap">{article.publishDate}</span>
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-eye-line"></i>{(article.viewCount / 10000).toFixed(0)}万
                      </span>
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-message-2-line"></i>{article.commentCount}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {articles.length > 0 && (
            <div className="mt-10 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 border border-background-300 text-foreground-700 font-medium rounded-lg hover:bg-background-200 transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}